// ──────────────────────────────────────────────────────────────────────────
// Admin → Security/Code — Help Text Data Source
// ──────────────────────────────────────────────────────────────────────────
//
// Context (from Ryan's walkthrough video):
//   "We've put so much into our admin section related to security and
//    monitoring and everything. From the perspective of somebody who may
//    not have a cyber security background [...] they need to understand
//    what this all is and what it means and what it does. Having some
//    help icons on each of these things — like here's what this does,
//    why you would need it, how to interpret stuff, what does this mean,
//    what gets done about it — would be helpful."
//
// Coverage (15 screens — Security/Code sub-tabs plus Verification Feedback
// and AI Compliance which now live elsewhere in Admin):
//   1.  sso-logins           — SSO & Logins
//   2.  security-scan        — Security Scan Dashboard
//   3.  evidence-harvesting  — Evidence Harvesting
//   4.  compliance-drift     — Real-Time Compliance Drift
//   5.  control-mapping      — Control Mapping & Reusability
//   6.  ai-assistant         — Evidence AI Assistant
//   7.  change-impact        — Change Impact Analysis
//   8.  questionnaire-autofill — Questionnaire Auto-Fill
//   9.  vendor-management    — Vendor Management
//   10. asset-inventory      — Asset Inventory
//   11. vulnerability-management — Vulnerability Management
//   12. ip-whitelist         — IP Whitelist Management
//   13. security-documents   — Security Documents
//   14. verification-feedback — Verification Feedback (Verification & Accuracy → Feedback)
//   15. ai-compliance        — AI Compliance (Audit/Logs → AI Compliance)
//
// (Trust Portal has its own data file — trustPortalHelpText.ts.)
//
// Content status: PLACEHOLDER. Ryan mentioned in the video that markdown
// requirement docs live in Asana and can be fed to Claude to generate
// approved help text. When those land, swap each entry's four sections
// below — the component reads this file directly, so content edits are a
// pure data change with no code touches needed.
//
// The `relatedLinks` field is where the "AI Assistant-like" internal
// navigation hints live: "to do X, go here" style callouts. Each link
// dispatches a `jarvis-admin-navigate` custom event that Admin.tsx
// listens for and uses to switch the user to the target tab/sub-tab.

import type { HelpEntry } from './helpTextTypes';

export type AdminSecurityScreenId =
  | 'sso-logins'
  | 'security-scan'
  | 'evidence-harvesting'
  | 'compliance-drift'
  | 'control-mapping'
  | 'ai-assistant'
  | 'change-impact'
  | 'questionnaire-autofill'
  | 'vendor-management'
  | 'asset-inventory'
  | 'vulnerability-management'
  | 'ip-whitelist'
  | 'security-documents'
  | 'verification-feedback'
  | 'ai-compliance';

export const ADMIN_SECURITY_HELP_TEXT: Record<AdminSecurityScreenId, HelpEntry> = {
  'sso-logins': {
    title: 'SSO & Logins',
    whatItDoes:
      'Configures how users authenticate into Jarvis — including Single Sign-On (SSO) providers like Okta, Google Workspace, and Microsoft Entra. This is the authoritative entry point for enabling, testing, and disabling identity federation for your tenant.',
    howToNavigate:
      'The screen is organized into connection forms for each supported SSO provider. Expand a provider to see its connection settings; use the test button to verify the connection before enabling it for real users.',
    whatItContains:
      'The currently enabled SSO provider (if any), connection metadata (entity ID, callback URL), provisioning settings, and the fallback password-based login toggle. Changes here affect ALL users in the tenant immediately.',
    keyActions:
      'Enable or switch an SSO provider, run a live connection test, toggle password-based login as a fallback, and provision just-in-time user accounts for first-time logins.',
    relatedLinks: [
      {
        label: 'Review SSO-related audit events',
        description: 'See login attempts, SSO errors, and provisioning events in the audit log.',
        targetTopTab: 'audit-logs',
      },
    ],
  },

  'security-scan': {
    title: 'Security Scan Dashboard',
    whatItDoes:
      'Runs automated security scans against the Jarvis codebase and its dependencies, then surfaces the findings in a prioritized dashboard so the security team can triage and remediate vulnerabilities before they reach production.',
    howToNavigate:
      'The dashboard opens with a summary of the latest scan (total findings, severity breakdown, scan timestamp). Scroll down to see the per-finding table; click a finding to expand its full detail panel including affected file, remediation guidance, and CVE references where applicable.',
    whatItContains:
      'Latest scan results, severity breakdown (critical/high/medium/low), trend chart showing finding counts over time, a filterable table of individual findings, and the list of scheduled and manual scans.',
    keyActions:
      'Trigger a manual scan, acknowledge or dismiss findings, export the current scan results to CSV, and configure scheduled scans.',
    relatedLinks: [
      {
        label: 'Manage vulnerabilities in detail',
        description: 'Jump to the Vulnerability Management screen for full remediation workflow tracking.',
        targetSubTab: 'vulnerability-management',
      },
    ],
  },

  'evidence-harvesting': {
    title: 'Evidence Harvesting',
    whatItDoes:
      'Automatically collects compliance evidence from connected systems (GitHub, AWS, GCP, Azure, and more) and stores it in a central evidence library. This is the top of the evidence pipeline — everything downstream (Compliance Drift, AI Assistant, Questionnaires) reads from the artifacts this screen produces.',
    howToNavigate:
      'The top of the screen lists connected sources with their last-harvest timestamp and health status. Below that, a timeline shows recent harvest runs with counts of new / updated / failed evidence items. Click any source to configure its harvest schedule and scope.',
    whatItContains:
      'Connected evidence sources, per-source health and last-run status, recent harvest run history, counts of collected artifacts grouped by type (configs, logs, screenshots, exports), and the full evidence library search interface.',
    keyActions:
      'Add a new evidence source, trigger a manual harvest run, adjust the harvest schedule, and browse or search the harvested evidence library.',
    relatedLinks: [
      {
        label: 'Check for compliance drift',
        description: 'See which collected evidence has become stale or drifted from policy.',
        targetSubSubTab: 'compliance-drift',
      },
      {
        label: 'Map evidence to controls',
        description: 'See how harvested evidence maps to the compliance framework controls you care about.',
        targetSubSubTab: 'control-mapping',
      },
    ],
  },

  'compliance-drift': {
    title: 'Real-Time Compliance Drift',
    whatItDoes:
      'Monitors your compliance posture continuously and flags when something changes in a way that could break a control. If an IAM policy is loosened, an MFA enforcement is disabled, or a firewall rule is added, this screen is where you will see it first.',
    howToNavigate:
      'Top of the screen shows a live compliance score and counts of open findings by severity. The Compliance Timeline below lists individual drift events, each tagged with the affected control family, severity, and a short impact description. Click an event to see the full diff and suggested remediation.',
    whatItContains:
      'Current compliance score, open findings grouped by severity (critical / high / medium / low), a chronological timeline of drift events with filtering by severity and date range, and direct links to the underlying evidence that triggered each event.',
    keyActions:
      'Acknowledge or triage a drift event, run a manual drift check, export the current findings, and configure alert thresholds for real-time notifications.',
    relatedLinks: [
      {
        label: 'See where drift came from',
        description: 'Jump to Evidence Harvesting to find the source system that reported the drift.',
        targetSubSubTab: 'evidence-harvesting',
      },
      {
        label: 'Ask the AI Assistant',
        description: 'Ask natural-language questions about your current compliance posture.',
        targetSubSubTab: 'ai-assistant',
      },
    ],
  },

  'control-mapping': {
    title: 'Control Mapping & Reusability',
    whatItDoes:
      'Shows how a single piece of evidence or a single control maps across multiple compliance frameworks (SOC 2, ISO 27001, NIST, HIPAA, PCI DSS, etc.) so you can reuse the same evidence across audits instead of collecting it multiple times.',
    howToNavigate:
      'The main view is a matrix — rows are controls, columns are frameworks, cells show the mapping strength (exact, partial, or none). Filter by framework or by control family at the top to narrow the view. Click a cell to see which evidence items satisfy that mapping.',
    whatItContains:
      'The mapping matrix, a list of controls with reuse coverage percentages, framework-by-framework coverage summaries, and the list of evidence items behind each mapping.',
    keyActions:
      'Filter by framework or control family, export the mapping matrix, drill into any control for its evidence trail, and suggest a new mapping to add to the library.',
    relatedLinks: [
      {
        label: 'Harvest more evidence',
        description: 'If a control has no evidence mapped, harvest more from the source systems.',
        targetSubSubTab: 'evidence-harvesting',
      },
    ],
  },

  'ai-assistant': {
    title: 'Evidence AI Assistant',
    whatItDoes:
      'A natural-language assistant that lets you ask questions about your collected compliance evidence in plain English. "What is our password policy?" "Do we encrypt data in transit?" "Are our backups tested?" — the assistant searches the evidence library, cross-references framework requirements, and returns a grounded answer with links back to the underlying artifacts.',
    howToNavigate:
      'Type a question in the chat input at the bottom and press Enter. Responses appear above with inline citations — click any citation to jump to the underlying evidence item. The left-side suggested queries panel surfaces common questions to help you get started.',
    whatItContains:
      'The chat transcript, inline evidence citations, a side panel of suggested queries, and a feedback control so you can flag answers that were wrong or incomplete. The assistant only uses evidence that has already been harvested — it will not invent information.',
    keyActions:
      'Ask a natural-language question, follow a cited evidence link, copy the response to share, flag a response as helpful or incorrect, and start a new conversation thread.',
    relatedLinks: [
      {
        label: 'Go to the source evidence',
        description: 'See the raw evidence items that back up the assistant\'s answers.',
        targetSubSubTab: 'evidence-harvesting',
      },
      {
        label: 'Check current drift',
        description: 'If the assistant says something has changed recently, review the drift timeline.',
        targetSubSubTab: 'compliance-drift',
      },
    ],
  },

  'change-impact': {
    title: 'Change Impact Analysis',
    whatItDoes:
      'Simulates infrastructure or policy changes before you make them and forecasts the compliance impact — will this change break any control? Affect any framework coverage? Loosen any existing posture? The goal is to catch compliance regressions at design time, not after they have shipped.',
    howToNavigate:
      'Describe the proposed change in the input form (either as free text or by uploading a diff). The analysis engine runs for a few seconds and produces an impact report showing affected controls, expected compliance score delta, and recommended mitigations.',
    whatItContains:
      'The change-description input, the analysis result summary (affected controls, severity, score delta), a per-control breakdown of which framework mappings would be affected, and suggested mitigation steps.',
    keyActions:
      'Describe a proposed change, run an impact analysis, export the analysis report, and save the analysis to the audit log for future reference.',
    relatedLinks: [
      {
        label: 'Review the current posture first',
        description: 'Before running an impact analysis, check the current drift screen for any open issues.',
        targetSubSubTab: 'compliance-drift',
      },
    ],
  },

  'questionnaire-autofill': {
    title: 'Questionnaire Auto-Fill',
    whatItDoes:
      'Takes a security questionnaire from a customer, regulator, or partner and automatically drafts answers using your collected evidence. Each answer is cited back to the underlying evidence so your security team only has to review and approve — not write from scratch.',
    howToNavigate:
      'Upload a questionnaire (CSV, Excel, or PDF) using the top control. Once processed, answers appear in a side-by-side view — question on the left, draft answer and citations on the right. Review each answer and approve, edit, or flag for manual attention.',
    whatItContains:
      'Uploaded questionnaires, per-question draft answers with confidence scores, inline evidence citations, approval status tracking, and an export to the original questionnaire format.',
    keyActions:
      'Upload a questionnaire, review and approve draft answers, edit any answer inline, export the completed questionnaire, and flag low-confidence answers for security-team review.',
    relatedLinks: [
      {
        label: 'Ask the AI Assistant directly',
        description: 'If a question is unusual, ask the assistant in conversational form.',
        targetSubSubTab: 'ai-assistant',
      },
      {
        label: 'Add missing evidence',
        description: 'If a question has no evidence to cite, go to Evidence Harvesting to add a source.',
        targetSubSubTab: 'evidence-harvesting',
      },
    ],
  },

  'vendor-management': {
    title: 'Vendor Management',
    whatItDoes:
      'Tracks the security, compliance, and risk posture of every third-party vendor your organization depends on. Think of it as your vendor risk register — every tier-1 and tier-2 vendor has a detailed profile with uploaded attestations, review history, and a current risk score.',
    howToNavigate:
      'The main view is a searchable vendor table with tier and risk filters at the top. Click any vendor row to open its detail pane, which has five tabs — Overview, Documents, Reviews, Contacts, and Onboarding. Use the Add button to register a new vendor.',
    whatItContains:
      'All registered vendors, their tier (1 = critical, 2 = standard, 3 = minor), current risk score, last review date, uploaded compliance documents (SOC 2 reports, ISO certificates, etc.), and the contact list for each vendor.',
    keyActions:
      'Add a new vendor, upload a compliance document to an existing vendor, run a new vendor review, archive a decommissioned vendor, and export the vendor list for audit reporting.',
    relatedLinks: [
      {
        label: 'See the Trust Portal view',
        description: 'Jump to the external-facing Trust Portal section with client-ready vendor profiles.',
        targetSubTab: 'trust-portal',
      },
    ],
  },

  'asset-inventory': {
    title: 'Asset Inventory',
    whatItDoes:
      'Maintains a live inventory of every IT asset your tenant depends on — servers, databases, SaaS applications, developer laptops — along with its owner, data classification, and compliance mapping. This is the foundation required by SOC 2 CC6.1/CC6.2 and ISO 27001 A.8.1.',
    howToNavigate:
      'The top shows summary counts (total assets, by type, by risk). The main table is searchable and filterable by type, owner, and classification. Click any asset row to open its detail pane with full metadata, dependency graph, and review history.',
    whatItContains:
      'All registered assets, their type (server, database, SaaS, endpoint), data classification (public / internal / confidential / restricted), owner, review status, linked dependencies, and the full audit trail of changes.',
    keyActions:
      'Add a new asset, reclassify an existing asset, link asset dependencies, initiate a review cycle, and export the inventory for audit submission.',
    relatedLinks: [
      {
        label: 'Add evidence about this asset',
        description: 'Link evidence items to assets by opening Evidence Harvesting.',
        targetSubSubTab: 'evidence-harvesting',
      },
    ],
  },

  'vulnerability-management': {
    title: 'Vulnerability Management',
    whatItDoes:
      'Tracks, prioritizes, and remediates security vulnerabilities discovered across your environment. Findings come from the Security Scan dashboard, external scanners, and manual disclosures; this screen is the single workflow where you triage them through to closure.',
    howToNavigate:
      'The dashboard shows open vulnerabilities grouped by severity at the top, followed by a filterable table. Click any vulnerability to see its full detail — affected asset, CVE reference, remediation guidance, and the remediation timeline. Use the top buttons to trigger a new scan or export the current list.',
    whatItContains:
      'Open vulnerabilities with severity and age, the full vulnerability history, remediation status tracking, CVE references, affected assets and owners, and SLA timers for each severity tier.',
    keyActions:
      'Triage a new vulnerability, assign a remediation owner, mark a vulnerability as fixed or accepted, run a new scan, export the current vulnerability list, and under Settings limit email alerts to Super Admins (or Super Admins + Admins).',
    relatedLinks: [
      {
        label: 'Run a fresh Security Scan',
        description: 'Kick off a new codebase scan to refresh findings.',
        targetSubTab: 'security-scan',
      },
      {
        label: 'See affected assets',
        description: 'Cross-reference vulnerabilities with the Asset Inventory.',
        targetSubTab: 'asset-inventory',
      },
    ],
  },

  'ip-whitelist': {
    title: 'IP Whitelist Management',
    whatItDoes:
      'Configures which IP addresses or CIDR ranges are allowed to access the Jarvis application and its internal admin APIs. Use this when you need to restrict access to the corporate VPN, bastion host, or known office networks.',
    howToNavigate:
      'The top of the screen shows the currently whitelisted ranges, grouped by access scope (app login, admin API, specific tenants). Use the Add button to register a new range; use the Test button to verify an IP against the current configuration before enabling enforcement.',
    whatItContains:
      'The current whitelist ranges with their scope and owner, enforcement status (active or bypass mode), change history for audit, and a live tester that lets you verify whether an arbitrary IP would be allowed or blocked.',
    keyActions:
      'Add a new IP range, edit or remove an existing range, toggle enforcement mode, test an IP against the current rules, and review the change history.',
    relatedLinks: [
      {
        label: 'Check who logged in recently',
        description: 'Cross-reference IP allow-list changes with login events in the audit log.',
        targetTopTab: 'audit-logs',
      },
    ],
  },

  'security-documents': {
    title: 'Security Documents',
    whatItDoes:
      'The central repository for all security-related documents — policies, standards, client attestations, SOC 2 reports, incident response runbooks, and anything else auditors might ask for. Documents here are version-controlled and tagged for easy retrieval.',
    howToNavigate:
      'Browse by folder on the left (Policies / Standards / Attestations / Runbooks), or use the top search bar to find a specific document by name, tag, or content. Click any document to preview it inline; use the download button to save a local copy.',
    whatItContains:
      'Uploaded security documents organized by folder, version history for each document, tags for quick search, upload and modification timestamps, and the full content preview for supported formats.',
    keyActions:
      'Upload a new document, replace an outdated version, add or edit tags, download a document, and review the upload history.',
    relatedLinks: [
      {
        label: 'Use documents in questionnaire answers',
        description: 'The Questionnaire Auto-Fill screen cites documents from this repository.',
        targetSubSubTab: 'questionnaire-autofill',
      },
    ],
  },

  'verification-feedback': {
    title: 'Verification Feedback',
    whatItDoes:
      'Captures and reviews verification feedback raised by users when AI-generated outputs need correction — flagged inaccuracies, missing context, or misclassifications. This is where reviewer notes are aggregated so the team can spot trends, retrain prompts, and improve accuracy over time.',
    howToNavigate:
      'The screen lists feedback events in reverse-chronological order with filters for severity, status (open / acknowledged / resolved), and source screen. Click any row to see the original AI output, the reviewer\'s note, and the suggested correction.',
    whatItContains:
      'Feedback entries (reviewer ID, timestamp, screen of origin, AI output snapshot, severity, status), aggregate metrics (total feedback per week, top-flagged screens, resolution rate), and the action history for each entry.',
    keyActions:
      'Filter feedback by severity / status / screen, drill into a single feedback event, mark an entry as acknowledged or resolved, and export feedback to share with the prompt-engineering team.',
    relatedLinks: [
      {
        label: 'See verification dashboards',
        description: 'Verification Dashboard shows the high-level accuracy KPIs that this feedback rolls up into.',
        targetTopTab: 'verification-accuracy',
      },
    ],
  },

  'ai-compliance': {
    title: 'AI Compliance',
    whatItDoes:
      'Tracks how the platform measures up against the NIST AI Risk Management Framework — Govern, Map, Measure, and Manage functions. Each control has a status, evidence references, and a last-reviewed timestamp so you can demonstrate AI accountability to auditors and clients on demand.',
    howToNavigate:
      'Switch between the four NIST functions (GOVERN / MAP / MEASURE / MANAGE) using the function tabs. Each function lists its categories and sub-controls; click any control to see linked evidence documents, the responsible owner, and the latest review notes.',
    whatItContains:
      'The current AI compliance matrix (framework version, last reviewed date, owner), per-control status (compliant / partial / not started), evidence document links, and a rolling activity window showing recent compliance-relevant events.',
    keyActions:
      'Browse the compliance matrix by NIST function, change the reporting window (7 / 30 / 90 / 180 days), refresh data from source systems, and export the matrix as a downloadable file for auditors.',
    relatedLinks: [
      {
        label: 'See related audit events',
        description: 'AI Activity log captures the underlying events that compliance status is derived from.',
        targetTopTab: 'audit-logs',
      },
    ],
  },
};

// Convenience accessor. Callers should always use this helper rather than
// indexing ADMIN_SECURITY_HELP_TEXT directly — that way, if an unknown
// screenId is ever passed in (e.g. from a typo in a new component), we
// fail soft with a clear fallback message instead of crashing.
export function getAdminSecurityHelpEntry(screenId: AdminSecurityScreenId): HelpEntry {
  const entry = ADMIN_SECURITY_HELP_TEXT[screenId];
  if (entry) return entry;
  return {
    title: 'Help content not available',
    whatItDoes: 'No help text has been configured for this screen yet.',
    howToNavigate: '',
    whatItContains: '',
    keyActions: '',
  };
}
